import { useEffect, useState, useCallback, useRef } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { Pulse, Heart, Warning, ShieldCheck, Airplane } from "@phosphor-icons/react";
import StatusDot from "@/components/StatusDot";
import CheckinHistory from "@/components/CheckinHistory";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const formatHMS = (totalSeconds) => {
  const s = Math.max(0, totalSeconds);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  return [h, m, sec].map((n) => String(n).padStart(2, "0")).join(":");
};

const formatTimestamp = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "—";
  }
};

const STATUS_COPY = {
  safe: {
    title: "Todo en orden",
    desc: "Última señal de vida registrada recientemente.",
    color: "text-aqua",
    glow: "drop-shadow-[0_0_25px_rgba(0,229,255,0.45)]",
    badgeBg: "bg-aqua/10",
    badgeBorder: "border-aqua/40",
    icon: Heart,
  },
  paused: {
    title: "En modo vacaciones",
    desc: "El temporizador está en pausa por un viaje o ausencia programada.",
    color: "text-warn",
    glow: "drop-shadow-[0_0_25px_rgba(255,153,0,0.4)]",
    badgeBg: "bg-warn/10",
    badgeBorder: "border-warn/40",
    icon: Airplane,
  },
  warning: {
    title: "Atención",
    desc: "Han pasado más de 24 horas sin un check-in.",
    color: "text-warn",
    glow: "drop-shadow-[0_0_25px_rgba(255,153,0,0.4)]",
    badgeBg: "bg-warn/10",
    badgeBorder: "border-warn/40",
    icon: ShieldCheck,
  },
  critical: {
    title: "Crítico",
    desc: "Queda menos de 1 hora antes de que se active la alerta.",
    color: "text-danger",
    glow: "drop-shadow-[0_0_25px_rgba(255,42,85,0.4)]",
    badgeBg: "bg-danger/10",
    badgeBorder: "border-danger/40",
    icon: Warning,
  },
  expired: {
    title: "Alerta activada",
    desc: "No ha registrado un check-in en el intervalo configurado. Contactos de emergencia notificados.",
    color: "text-danger",
    glow: "drop-shadow-[0_0_25px_rgba(255,42,85,0.6)]",
    badgeBg: "bg-danger/10",
    badgeBorder: "border-danger/50",
    icon: Warning,
  },
};

const PublicStatusPage = () => {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [seconds, setSeconds] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const tickRef = useRef(null);

  const fetchStatus = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/share/${token}`);
      setData(res.data);
      setSeconds(res.data.seconds_remaining);
      setError(null);
    } catch (e) {
      const msg = e?.response?.data?.detail || "No se pudo cargar el estado";
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchStatus();
    const sync = setInterval(fetchStatus, 30000);
    return () => clearInterval(sync);
  }, [fetchStatus]);

  useEffect(() => {
    if (tickRef.current) clearInterval(tickRef.current);
    tickRef.current = setInterval(() => {
      setSeconds((s) => (s > -86400 ? s - 1 : s));
    }, 1000);
    return () => clearInterval(tickRef.current);
  }, []);

  const statusKey = !data
    ? "safe"
    : data.paused
    ? "paused"
    : seconds <= 0
    ? "expired"
    : seconds < 3600
    ? "critical"
    : seconds < 24 * 3600
    ? "warning"
    : "safe";
  const cfg = STATUS_COPY[statusKey];
  const Icon = cfg.icon;

  if (loading) {
    return (
      <div
        className="min-h-screen flex items-center justify-center text-white/60 text-sm"
        data-testid="public-loading"
      >
        Cargando estado...
      </div>
    );
  }

  if (error) {
    return (
      <div
        className="min-h-screen flex flex-col items-center justify-center px-6 text-center"
        data-testid="public-error"
      >
        <Warning size={48} weight="duotone" className="text-danger mb-4" />
        <h1 className="font-heading font-black text-2xl mb-2">Enlace no disponible</h1>
        <p className="text-white/60 max-w-md">{error}</p>
      </div>
    );
  }

  const intervalSeconds = data.interval_hours * 3600;
  const fractionLeft = Math.max(0, Math.min(1, seconds / intervalSeconds));

  return (
    <div className="min-h-screen w-full text-white" data-testid="public-status-page">
      <header className="w-full border-b border-border/60">
        <div className="max-w-3xl mx-auto px-6 md:px-12 py-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Pulse size={26} weight="duotone" className="text-aqua" />
            <span className="font-heading font-black text-xl tracking-[0.4em]">
              AQUÍSIGO
            </span>
          </div>
          <div className="flex items-center gap-3">
            <StatusDot statusKey={statusKey} />
            <span className="text-xs uppercase tracking-[0.25em] text-white/70 hidden sm:inline">
              Estado público
            </span>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 md:px-12 py-12 md:py-16">
        <p className="text-xs uppercase tracking-[0.4em] text-white/45 mb-3">
          Estado de seguridad de
        </p>
        <h1
          className="font-heading font-black text-4xl sm:text-5xl lg:text-6xl tracking-tight mb-3"
          data-testid="public-owner-name"
        >
          {data.owner_name}
        </h1>
        <p className="text-base text-white/60 mb-12 max-w-xl">
          Esta página muestra en tiempo real el último check-in registrado. Si la cuenta
          regresiva llega a cero, sus contactos de emergencia recibirán un aviso automático.
        </p>

        {/* Big badge */}
        <div
          className={`flex items-center gap-4 p-5 rounded-2xl ${cfg.badgeBg} border ${cfg.badgeBorder} mb-10`}
          data-testid="public-status-badge"
        >
          <div className={`shrink-0 ${cfg.color}`}>
            <Icon size={40} weight="duotone" />
          </div>
          <div>
            <div className={`font-heading font-bold text-xl ${cfg.color}`}>
              {cfg.title}
            </div>
            <div className="text-sm text-white/70 mt-0.5">{cfg.desc}</div>
          </div>
        </div>

        {/* Countdown */}
        <section className="mb-10 text-center">
          <p className="text-xs uppercase tracking-[0.35em] text-white/45 mb-3">
            {data.paused
              ? "Pausado hasta"
              : seconds <= 0
              ? "Tiempo expirado"
              : "Tiempo hasta próxima alerta"}
          </p>
          {data.paused ? (
            <div
              className={`mono font-mono text-2xl sm:text-3xl font-medium tracking-tight ${cfg.color} ${cfg.glow}`}
              data-testid="public-paused-until"
            >
              {formatTimestamp(data.vacation_until)}
            </div>
          ) : (
            <div
              className={`mono font-mono tabular-nums text-5xl sm:text-7xl font-medium tracking-tight ${cfg.color} ${cfg.glow}`}
              data-testid="public-countdown"
            >
              {seconds <= 0 ? "00:00:00" : formatHMS(seconds)}
            </div>
          )}
        </section>

        {/* Progress */}
        <section className="mb-10">
          <div className="flex items-center justify-between text-xs uppercase tracking-[0.25em] text-white/50 mb-2">
            <span>Tiempo restante del ciclo</span>
            <span className="mono font-mono">
              {data.paused ? "PAUSA" : `${Math.round(fractionLeft * 100)}%`}
            </span>
          </div>
          <div className="h-1.5 w-full bg-surface-elevated rounded-full overflow-hidden border border-border">
            <motion.div
              className={
                "h-full " +
                (data.paused
                  ? "bg-warn"
                  : statusKey === "expired" || statusKey === "critical"
                  ? "bg-danger"
                  : statusKey === "warning"
                  ? "bg-warn"
                  : "bg-aqua")
              }
              initial={false}
              animate={{ width: data.paused ? "100%" : `${fractionLeft * 100}%` }}
              transition={{ duration: 0.6, ease: "easeOut" }}
            />
          </div>
        </section>

        {/* Details */}
        <section className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-10">
          <div
            className="bg-surface border border-border p-5 rounded-xl"
            data-testid="public-widget-last"
          >
            <div className="text-[10px] font-bold text-white/45 uppercase tracking-[0.25em]">
              Último check-in
            </div>
            <div className="mono font-mono text-base text-white mt-2">
              {formatTimestamp(data.last_checkin)}
            </div>
          </div>
          <div
            className="bg-surface border border-border p-5 rounded-xl"
            data-testid="public-widget-deadline"
          >
            <div className="text-[10px] font-bold text-white/45 uppercase tracking-[0.25em]">
              Próximo deadline
            </div>
            <div className="mono font-mono text-base text-white mt-2">
              {formatTimestamp(data.next_deadline)}
            </div>
          </div>
        </section>

        {/* Check-in history */}
        <section className="mb-12">
          <CheckinHistory items={data.checkin_history ?? []} />
        </section>

        <div className="border-t border-border/60 pt-6 text-xs text-white/40">
          Esta es una página pública compartida. No contiene información personal sensible.
          Los datos se actualizan automáticamente cada 30 segundos.
        </div>
      </main>
    </div>
  );
};

export default PublicStatusPage;
